import {DataStatus, type FormControl} from 'platform/components';

import {useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {type CampaignStatusValue, useListCampaignsQuery} from '@dms/api/sales';
import i18n from '@dms/i18n';

import {Nullish, RequiredTestIdProps, suffixTestId} from 'shared/web';

import {CampaignFormValues} from '../campaignFormSchema';
import {CampaignTargeting} from '../types/CampaignTargeting';
import {doCampaignPeriodsOverlap} from '../utils/doCampaignPeriodsOverlap';
import {CampaignCollisionAlert} from './CampaignCollisionAlert';

const COLLIDABLE_STATUSES: CampaignStatusValue[] = ['ACTIVE', 'SCHEDULED'];

interface CampaignCollisionListProps extends RequiredTestIdProps {
  control: FormControl<CampaignFormValues>;
  campaignId: string | Nullish;
}

export function CampaignCollisionList(props: CampaignCollisionListProps) {
  const values = useWatch({control: props.control});
  const {data: campaigns, isLoading, isError} = useListCampaignsQuery();

  const targeting: CampaignTargeting = {
    isAppliedToAllVehicles: values.isAppliedToAllVehicles ?? false,
    filter: values.filter,
  };

  const {validFrom, validTo} = values;

  const periodOverlappingCampaigns =
    isNotNil(validFrom) && isNotNil(validTo)
      ? (campaigns ?? []).filter(
          (campaign) =>
            COLLIDABLE_STATUSES.includes(campaign.status) &&
            campaign.id !== props.campaignId &&
            doCampaignPeriodsOverlap({from: validFrom, to: validTo}, campaign)
        )
      : [];

  return (
    <DataStatus
      isLoading={isLoading}
      isError={isError}
      errorMessage={i18n.t('entity.salesCampaign.hints.campaignCollisionCheckFailed')}
      minHeight={10}
    >
      {periodOverlappingCampaigns.map((campaign) => (
        <CampaignCollisionAlert
          key={campaign.id}
          campaign={campaign}
          targeting={targeting}
          data-testid={suffixTestId(campaign.id, props)}
        />
      ))}
    </DataStatus>
  );
}
